import React, { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useTask } from "../../contexts/TaskContext";
import {
  DescriptionDetails,
  DescriptionList,
  DescriptionTerm,
} from "../../design/description-list";
import { Heading } from "../../design/heading";
import { Divider } from "../../design/divider";
import DeleteTaskButton from "./components/DeleteTaskButton";
import LoadingView from "../../components/LoadingView";
import EditTaskButton from "./components/EditTaskButton";
import ErrorView from "../../components/ErrorView";
import Breadcrumbs from "../../components/Breadcrumbs";
import SubtasksList from "./components/SubtaskList/SubTaskList";
import { User } from "../../models/User";

const TaskDetailPage: React.FC = () => {
  const { taskId } = useParams<{ taskId: string }>();
  const { selectedTask, setSelectedTask, error, loading } = useTask();

  useEffect(() => {
    if (taskId) {
      setSelectedTask(taskId);
    }
  }, [taskId]);

  if (loading.details) {
    return <LoadingView />;
  }

  if (error.details) {
    return <ErrorView error={error.details} />;
  }

  if (!selectedTask) {
    return <ErrorView error={new Error("Task not found")} />;
  }

  return (
    <div>
      <Breadcrumbs
        items={[
          { label: "Tasks", href: "/tasks" },
          { label: selectedTask._id, href: `/task/${selectedTask._id}` },
        ]}
      />

      <div className="flex flex-col gap-6 lg:flex-row">
        <div className="flex flex-col w-full gap-6">
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <Heading>{selectedTask.title}</Heading>
              <div className="flex gap-2">
                <EditTaskButton task={selectedTask} />
                <DeleteTaskButton taskId={selectedTask._id} />
              </div>
            </div>
            <DescriptionList>
              {selectedTask.assignedTo && (
                <>
                  <DescriptionTerm>Assigned To</DescriptionTerm>
                  <DescriptionDetails>
                    {selectedTask.assignedTo
                      .map((user) => (user as User)?.name)
                      .join(", ")}
                  </DescriptionDetails>
                </>
              )}
              {selectedTask.description && (
                <>
                  <DescriptionTerm>Description</DescriptionTerm>
                  <DescriptionDetails style={{ whiteSpace: "pre-line" }}>
                    {selectedTask.description}
                  </DescriptionDetails>
                </>
              )}
            </DescriptionList>
          </div>
          <Divider className="mt-2" />
          <SubtasksList taskId={taskId!} />
        </div>
      </div>
    </div>
  );
};

export default TaskDetailPage;
